import React from 'react';
import type { ColumnDef, StockFeatures } from '@tanstack/react-table';
import {
  Button,
  cn,
  DataTable,
  EmptyState,
  Flex,
  FilterToolbar,
  FilterToolbarChips,
  FilterToolbarDivider,
  FilterToolbarSearch,
  Menu,
  MenuItem,
  MenuTrigger,
  normalizeRunStatus,
  StatusBadge,
  TagBadge,
  Text,
  ToolbarFilterMenu,
  type FilterToolbarChip,
  type MultiSelectOption,
  type Row,
} from '@chatsdet/ui';
import { ArrowClockwise, ArrowSquareOut, DotsThreeVertical } from '@phosphor-icons/react';
import { RETRYABLE_TEST_RUN_STATUSES } from '@chatsdet/types';

import {
  formatDuration,
  getAgenticTestRunFailureSubtext,
  type AgenticTestRunDto,
  type WorkflowTestRunStatus,
} from '../../../features/workflows/useAgenticRunDetail';
import { getRerunButtonState } from '../../../features/workflows/rerunAvailability';
import type { TestRunDetailSearchParams } from '../../../router';
import { AttemptHistory } from './AttemptHistory';
import { testRunLabelMap } from './statusLabels';

/**
 * "Failures only" uses the same definition of failure as the header's "Retry all
 * failed" action, so the toggle and that count always agree. Negating the passing
 * statuses instead would sweep in queued/running/skipped rows, which are not failures.
 */
const isFailureStatus = (status: WorkflowTestRunStatus): boolean =>
  RETRYABLE_TEST_RUN_STATUSES.has(status);

/**
 * Stable, static id per label — a slug of the label text — so an option's id
 * never depends on which raw status a given run happened to have. Without
 * this, grouping by whatever status appeared first in `testRuns` meant the
 * "Passed" option's id flip-flopped between `success` and `flaky_success` as
 * polling reordered rows, silently breaking any URL holding the old id.
 */
const slugifyLabel = (label: string): string => label.toLowerCase().replace(/[^a-z0-9]+/g, '-');

/** Shared empty array so the no-status-filter case keeps a stable reference. */
const EMPTY_STATUSES: string[] = [];

/** All possible status groups, derived once from the closed label map. */
const ALL_STATUS_GROUPS: { id: string; label: string; statuses: string[] }[] = (() => {
  const byLabel = new Map<string, string[]>();
  for (const [status, label] of Object.entries(testRunLabelMap)) {
    const statuses = byLabel.get(label);
    if (!statuses) byLabel.set(label, [status]);
    else statuses.push(status);
  }
  return Array.from(byLabel, ([label, statuses]) => ({ id: slugifyLabel(label), label, statuses }));
})();

type AgenticRunFailuresTableProps = {
  testRuns: AgenticTestRunDto[];
  isRunning: boolean;
  detailRowsUnavailable: boolean;
  completedWorkflows: number;
  totalWorkflows: number;
  getRowHref: (testRun: AgenticTestRunDto) => string | undefined;
  onOpenTestRunDetail: (testRunId: string) => void;
  onRerun: (testRunId: string) => void;
  onViewWorkflow: (workflowId: string) => void;
  isRerunPending: boolean;
  filters: TestRunDetailSearchParams;
  onFilterChange: (updates: Partial<TestRunDetailSearchParams>) => void;
};

export const AgenticRunFailuresTable: React.FC<AgenticRunFailuresTableProps> = ({
  testRuns,
  isRunning,
  detailRowsUnavailable,
  completedWorkflows,
  totalWorkflows,
  getRowHref,
  onOpenTestRunDetail,
  onRerun,
  onViewWorkflow,
  isRerunPending,
  filters,
  onFilterChange,
}) => {
  const [searchInput, setSearchInput] = React.useState(filters.q ?? '');

  React.useEffect(() => {
    setSearchInput(filters.q ?? '');
  }, [filters.q]);

  React.useEffect(() => {
    const handle = window.setTimeout(() => {
      const trimmed = searchInput.trim();
      if ((filters.q ?? '') === trimmed) return;
      onFilterChange({ q: trimmed || undefined });
    }, 350);
    return () => window.clearTimeout(handle);
  }, [searchInput, filters.q, onFilterChange]);

  /**
   * One option per user-facing label, not per raw status: `success` and
   * `flaky_success` both read as "Passed", so keying options off the raw status
   * put "Passed" in the menu twice. Groups themselves come from the static
   * `ALL_STATUS_GROUPS` (stable ids independent of `testRuns`); this only
   * decides which of those groups are worth offering for this run's data.
   */
  const statusGroups = React.useMemo(() => {
    const presentLabels = new Set(testRuns.map((run) => testRunLabelMap[run.status] ?? run.status));
    // ToolbarFilterMenu sorts the options it renders, so no ordering here.
    return ALL_STATUS_GROUPS.filter((group) => presentLabels.has(group.label));
  }, [testRuns]);

  const statusOptions = React.useMemo<MultiSelectOption[]>(
    () => statusGroups.map(({ id, label }) => ({ id, label })),
    [statusGroups]
  );

  // Stable reference for the common no-filter case: `filters.statuses ?? []`
  // inline would allocate a fresh array on every keystroke of the search box,
  // invalidating every memo below it.
  const selectedStatuses = filters.statuses ?? EMPTY_STATUSES;

  /**
   * Selected option ids expanded to every raw status they stand for. Ids
   * come from the closed `ALL_STATUS_GROUPS` set, so an id that doesn't
   * resolve to a group (e.g. a stale or hand-edited URL param) contributes
   * nothing rather than being treated as a raw status itself.
   */
  const selectedStatusSet = React.useMemo(() => {
    if (selectedStatuses.length === 0) return null;
    const set = new Set<string>();
    for (const id of selectedStatuses) {
      const group = ALL_STATUS_GROUPS.find((candidate) => candidate.id === id);
      if (group) group.statuses.forEach((status) => set.add(status));
    }
    return set;
  }, [selectedStatuses]);

  const { q, failuresOnly, retriedOnly } = filters;

  const filteredTestRuns = React.useMemo(() => {
    const query = (q ?? '').trim().toLowerCase();
    return testRuns.filter((run) => {
      if (selectedStatusSet && !selectedStatusSet.has(run.status)) return false;
      if (failuresOnly && !isFailureStatus(run.status)) return false;
      if (retriedOnly && run.attempt <= 1) return false;
      if (query) {
        const name = (run.workflowName ?? '').toLowerCase();
        const shortId = run.workflowId.slice(0, 8).toLowerCase();
        if (!name.includes(query) && !shortId.includes(query)) return false;
      }
      return true;
    });
  }, [testRuns, q, failuresOnly, retriedOnly, selectedStatusSet]);

  const activeFilterChips = React.useMemo<FilterToolbarChip[]>(() => {
    const chips: FilterToolbarChip[] = [];
    if (q) chips.push({ id: 'q', label: `Search: ${q}` });
    selectedStatuses.forEach((status) => {
      const label =
        ALL_STATUS_GROUPS.find((group) => group.id === status)?.label ??
        testRunLabelMap[status] ??
        status;
      chips.push({ id: `status:${status}`, label: `Status: ${label}` });
    });
    if (failuresOnly) chips.push({ id: 'failuresOnly', label: 'Failures only' });
    if (retriedOnly) chips.push({ id: 'retriedOnly', label: 'Retried' });
    return chips;
  }, [q, failuresOnly, retriedOnly, selectedStatuses]);

  const handleClearAll = React.useCallback(() => {
    setSearchInput('');
    onFilterChange({
      q: undefined,
      statuses: undefined,
      failuresOnly: undefined,
      retriedOnly: undefined,
    });
  }, [onFilterChange]);

  const handleChipRemove = React.useCallback(
    (chipId: string) => {
      if (chipId === 'q') {
        setSearchInput('');
        onFilterChange({ q: undefined });
      } else if (chipId.startsWith('status:')) {
        const status = chipId.split(':', 2)[1];
        const nextStatuses = (filters.statuses ?? []).filter((s) => s !== status);
        onFilterChange({ statuses: nextStatuses.length > 0 ? nextStatuses : undefined });
      } else if (chipId === 'failuresOnly') {
        onFilterChange({ failuresOnly: undefined });
      } else if (chipId === 'retriedOnly') {
        onFilterChange({ retriedOnly: undefined });
      }
    },
    [filters.statuses, onFilterChange]
  );

  const hasActiveFilters = activeFilterChips.length > 0;

  const columns = React.useMemo<ColumnDef<StockFeatures, AgenticTestRunDto>[]>(
    () => [
      {
        accessorKey: 'workflowId',
        header: 'Workflow',
        cell: ({ row }) => {
          const results = row.original.results ?? [];
          const passed = results.filter((result) => result.status === 'success').length;
          const failed = results.filter((result) => result.status === 'failure').length;
          const total = results.length;
          const failureSubtext = getAgenticTestRunFailureSubtext(row.original);
          const shortId = row.original.workflowId.slice(0, 8);

          return (
            <div className="min-w-0 py-0.5">
              {/*
                Plain content, not a nested button: the row itself is already a
                link to this exact test run, so a button here would duplicate the
                destination while breaking cmd-click-to-new-tab over its area.

                The status badge sits on the meta line below, not before the
                name — leading it indented every title by the badge's width, so
                titles no longer lined up down the column.
              */}
              <Flex align="center" gap="xs" className="min-w-0">
                <Text
                  size="sm"
                  className="truncate font-medium min-w-0"
                  title={row.original.workflowName ?? row.original.workflowId}
                >
                  {row.original.workflowName ?? 'Unnamed Flow'}
                </Text>
                {row.original.consecutiveFailures === 1 && (
                  <TagBadge name="NEW" color="yellow" hideDot size="sm" />
                )}
                {row.original.consecutiveFailures > 1 && (
                  <Text as="span" size="xs" className="text-muted-foreground whitespace-nowrap">
                    {row.original.consecutiveFailures} in a row
                  </Text>
                )}
                {row.original.attempt > 1 && (
                  <TagBadge
                    name={`Attempt ${row.original.attempt}`}
                    color="blue"
                    hideDot
                    size="sm"
                    tooltip={`This flow was retried — attempt ${row.original.attempt} shown, earlier attempts below.`}
                  />
                )}
              </Flex>

              <Flex align="center" gap="xs" className="flex-wrap mt-1">
                <StatusBadge
                  status={normalizeRunStatus(row.original.status)}
                  label={testRunLabelMap[row.original.status]}
                  size="sm"
                />
                {/* Scenario is set on a small minority of runs — a column of
                    em dashes, so it rides the meta line and only when present. */}
                {row.original.scenarioName && (
                  <TagBadge name={row.original.scenarioName} color="purple" size="sm" hideDot />
                )}
                <Text size="xs" className="text-muted-foreground">
                  {shortId}
                </Text>
                {row.original.startedAt && (
                  <>
                    <Text size="xs" className="text-muted-foreground">
                      ·
                    </Text>
                    <Text size="xs" className="text-muted-foreground">
                      {formatDuration(row.original.startedAt, row.original.finishedAt)}
                    </Text>
                  </>
                )}
                {total > 0 && (
                  <>
                    <Text size="xs" className="text-muted-foreground">
                      ·
                    </Text>
                    <Text size="xs">
                      <span
                        className={
                          passed > 0
                            ? 'text-green-600 dark:text-green-400'
                            : 'text-muted-foreground'
                        }
                      >
                        {passed}
                      </span>
                      <span className="text-muted-foreground">/</span>
                      <span className={failed > 0 ? 'text-destructive' : 'text-muted-foreground'}>
                        {total}
                      </span>
                    </Text>
                  </>
                )}
              </Flex>

              {failureSubtext && (
                <Text
                  size="xs"
                  className="text-destructive line-clamp-3 break-all mt-0.5"
                  title={failureSubtext}
                >
                  {failureSubtext}
                </Text>
              )}
            </div>
          );
        },
        meta: { flex: 6 },
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => {
          const rerunState = getRerunButtonState(row.original.rerunAvailability, isRerunPending);
          return (
            <div onClick={(event) => event.stopPropagation()}>
              <MenuTrigger>
                <Button
                  variant="ghost"
                  size="sm"
                  iconOnly
                  className="p-2 text-muted-foreground"
                  aria-label="Test run actions"
                >
                  <DotsThreeVertical size={16} weight="bold" />
                </Button>
                <Menu
                  onAction={(key) => {
                    if (key === 'rerun') {
                      onRerun(row.original.id);
                    } else if (key === 'open-flow') {
                      onViewWorkflow(row.original.workflowId);
                    }
                  }}
                >
                  <MenuItem id="rerun" isDisabled={rerunState.isDisabled}>
                    <ArrowClockwise size={14} className="mr-2 shrink-0" />
                    <div className="flex flex-col">
                      <span>Rerun</span>
                      {rerunState.isDisabled && (
                        <span className="text-xs text-muted-foreground">{rerunState.tooltip}</span>
                      )}
                    </div>
                  </MenuItem>
                  {/* No "open run details" item — the row itself links there. */}
                  <MenuItem id="open-flow">
                    <ArrowSquareOut size={14} className="mr-2" />
                    Open flow
                  </MenuItem>
                </Menu>
              </MenuTrigger>
            </div>
          );
        },
        meta: { flex: 0.3, maxWidth: 50 },
      },
    ],
    [isRerunPending, onRerun, onViewWorkflow]
  );

  // Table.tsx memoizes its expander column and table options on this
  // object's identity — an inline literal here would rebuild both on every
  // `searchInput` keystroke, since that state lives on this component.
  const expandableRows = React.useMemo(
    () => ({
      getIsRowExpandable: (row: Row<AgenticTestRunDto>) => row.original.attempt > 1,
      render: (row: Row<AgenticTestRunDto>) => (
        <AttemptHistory testRunId={row.original.id} onOpenTestRunDetail={onOpenTestRunDetail} />
      ),
    }),
    [onOpenTestRunDetail]
  );

  if (testRuns.length === 0) {
    return detailRowsUnavailable ? (
      <EmptyState
        title="Workflow detail rows are unavailable"
        description={`This run recorded ${completedWorkflows}/${totalWorkflows} completed workflows, but the per-workflow rows are missing.`}
      />
    ) : (
      <EmptyState
        title={isRunning ? 'Waiting on the first workflow' : 'No workflow tests in this run'}
        description={
          isRunning
            ? 'Workflow tests will appear here as they start.'
            : 'Nothing was executed as part of this test run.'
        }
      />
    );
  }

  return (
    <div>
      <FilterToolbar attached>
        <FilterToolbarSearch
          aria-label="Search workflow tests"
          placeholder="Search workflows..."
          value={searchInput}
          onChange={setSearchInput}
        />

        <ToolbarFilterMenu
          label="Status"
          options={statusOptions}
          selectedIds={selectedStatuses}
          onChange={(ids) => onFilterChange({ statuses: ids.length > 0 ? ids : undefined })}
        />

        <FilterToolbarDivider />

        <Button
          variant="ghost"
          size="sm"
          onPress={() => onFilterChange({ failuresOnly: filters.failuresOnly ? undefined : true })}
          aria-pressed={!!filters.failuresOnly}
          className={cn(
            'text-muted-foreground',
            filters.failuresOnly && 'bg-destructive/10 text-destructive'
          )}
        >
          Failures only
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onPress={() => onFilterChange({ retriedOnly: filters.retriedOnly ? undefined : true })}
          aria-pressed={!!filters.retriedOnly}
          className={cn(
            'text-muted-foreground',
            filters.retriedOnly && 'bg-primary/10 text-primary'
          )}
        >
          Retried
        </Button>

        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground"
            onPress={handleClearAll}
          >
            Clear
          </Button>
        )}

        <FilterToolbarChips chips={activeFilterChips} onRemove={handleChipRemove} />
      </FilterToolbar>

      {filteredTestRuns.length === 0 ? (
        <EmptyState
          className="rounded-t-none border-t-0"
          title="No workflows match these filters"
          description="Try a different status, or clear the filters to see the whole run."
          action={
            <Button variant="outline" size="sm" onPress={handleClearAll}>
              Clear filters
            </Button>
          }
        />
      ) : (
        <DataTable
          columns={columns}
          data={filteredTestRuns}
          aria-label="Workflow test runs"
          getRowHref={getRowHref}
          containerClassName="rounded-t-none filter-lockup-table"
          expandableRows={expandableRows}
        />
      )}
    </div>
  );
};
